import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import _ from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import Information from "./_information";
import { RentalForm } from "@/types/rental.type";
import { useEditRentalMutation, useGetRentalQuery } from "@/services/rental.service";
import { formatDateTimeAutoToString } from "@/utils/format";
import moment from "moment-timezone";

export default function EditRental() {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const {state: {tenant_id, id}} = location
    const [editRental, {isSuccess}] = useEditRentalMutation()
    const {data: rental} = useGetRentalQuery(id || "", {refetchOnMountOrArgChange: true})
    const methods = useForm<RentalForm>()
    const {handleSubmit, setValue} = methods

    const onSubmit = handleSubmit(async (data) => {
        if(rental) {
            let startDate = rental.start_date
            let endDate = rental.end_date
            let depositDate = rental.deposit_date
            let now = moment();
            if (formatDateTimeAutoToString(rental.start_date, "YYYY-MM-DD") !== data.start_date) {
                startDate = moment(`${data.start_date} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            if (formatDateTimeAutoToString(rental.end_date, "YYYY-MM-DD") !== data.end_date) {
                endDate = moment(`${data.end_date} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            if (formatDateTimeAutoToString(rental.deposit_date, "YYYY-MM-DD") !== data.deposit_date) {
                depositDate = moment(`${data.deposit_date} ${now.hours()}:${now.minutes()}:${now.seconds()}.${now.milliseconds()}`, 'YYYY-MM-DD HH:mm:ss.SSS').toISOString()
            }
            editRental({
                ...{
                    ...data,
                    start_date: startDate,
                    end_date: endDate,
                    deposit_date: depositDate,
                    rent_amount: Number(data.rent_amount),
                    deposit_amount: Number(data.deposit_amount),
                    late_fee: !_.isEmpty(data.late_fee) ? Number(data.late_fee) : 0,
                    late_grace: data.late_grace,
                    type: Number(data.type)
                }, 
                id: rental?.id,
            })
        }
    })

    useEffect(() => {
        if (rental) {
            setValue("tenant_id", tenant_id ? tenant_id : rental.tenant_id)
            setValue("business_unit_id", rental.room.business_unit_id)
            setValue("room_id", rental.room_id)
            setValue("start_date", formatDateTimeAutoToString(rental.start_date, "YYYY-MM-DD"))
            setValue("end_date", formatDateTimeAutoToString(rental.end_date, "YYYY-MM-DD"))
            setValue("rent_amount", rental.rent_amount)
            setValue("deposit_amount", rental.deposit_amount)
            setValue("deposit_date", formatDateTimeAutoToString(rental.deposit_date, "YYYY-MM-DD"))
            setValue("agent_name", rental.agent_name)
            setValue("ta_ref_no", rental.ta_ref_no)
            rental.late_fee > 0 && setValue("late_fee", rental.late_fee)
            rental.late_grace > 0 && setValue("late_grace", rental.late_grace)
            setValue("tenant_ids", rental.rental_tenants.map((rt) => rt.tenant_id))
            setValue("type", rental.type)
        }
    }, [rental])

    useEffect(() => {
        if(isSuccess) {
            navigate(location, {state: {reload: true}})
        }
    }, [isSuccess])

    return (
        <div x-show="modalOpen" x-transition="" className="fixed top-0 left-0 z-99999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
            <div className="w-full max-w-screen-lg rounded-lg bg-white py-5 px-8 dark:bg-boxdark">
                <h3 className="text-xl dark:text-white text-center font-medium mb-5">
                    {t("rental.edit_rental")}
                </h3>
                <form className="mx-5" onSubmit={onSubmit}>
                    <FormProvider {...methods}>
                        <Information />
                    </FormProvider>
                    <div className="flex justify-end">
                        <div className="px-3">
                            <button type="button" onClick={() => navigate(location)} className="block w-full rounded border border-stroke bg-gray-400 p-1.5 px-3 text-center font-medium text-black transition hover:bg-opacity-90">
                                {t("modal.cancel")}
                            </button>
                        </div>
                        <div className="px-3">
                            <button type="submit" className="flex w-full justify-center rounded bg-primary p-1.5 px-3 text-white hover:bg-opacity-90">
                                {t("modal.submit")}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}
